// Shared vocabulary of HR › Payroll › Pay schedules (Payroll Gate 3). The rules are the server's
// (pay-schedule.rules.js / pay-schedule.service.js); this file only says them in an operator's words.
//
// Words kept apart on purpose (report 46 §9, hr-revamp/51 §11):
//   pay schedule  — how often payroll runs and when it pays (twice a month, every two weeks, weekly)
//   salary basis  — how an employee's pay is stated (per month, per day, per hour …) on Employee › Pay
//   work schedule — the shifts and days an employee works (HR › Shifts)

export const CADENCE = {
  semi_monthly: "Twice a month",
  bi_weekly: "Every two weeks",
  weekly: "Every week",
  monthly: "Once a month (no longer used)",
};

export const STATUS = {
  draft: { label: "Draft", tone: "neutral" },
  active: { label: "Active", tone: "success" },
  archived: { label: "Archived", tone: "neutral" },
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function dayText(value) {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-PH", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
}

const ordinal = (n) => `${n}${[11, 12, 13].includes(n % 100) ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th")}`;

/** "1st – 15th and 16th – month end, paid 5 days after each cutoff" */
export function ruleText(schedule) {
  if (!schedule) return "";
  const delay = Number(schedule.payDelayDays || 0);
  const paid = delay ? `paid ${delay} day${delay === 1 ? "" : "s"} after each cutoff` : "paid on the cutoff day";
  if (schedule.frequency === "semi_monthly") {
    const cut = Number(schedule.semiMonthlyCutoffDay || 15);
    return `1st – ${ordinal(cut)} and ${ordinal(cut + 1)} – month end, ${paid}`;
  }
  if (schedule.frequency === "weekly" || schedule.frequency === "bi_weekly") {
    const start = schedule.anchorDate ? WEEKDAYS[new Date(`${schedule.anchorDate}T00:00:00Z`).getUTCDay()] : null;
    return `${schedule.frequency === "weekly" ? "One week" : "Two weeks"} at a time${start ? `, starting on ${start}s` : ""}, ${paid}`;
  }
  return paid;
}

/** The next period that has not ended yet, from the server's list of expected windows. */
export function nextWindow(schedule, today) {
  const dates = schedule?.nextPayDates || [];
  return dates.find((row) => !today || row.endDate >= today) || dates[0] || null;
}

/** Plain consequence of each lifecycle action, for the confirmations (DESIGN.md › Consequential actions). */
export const LIFECYCLE_COPY = {
  activate: {
    title: (s) => `Activate "${s.name}"?`,
    consequence: "From its first period, payroll periods are created for whoever is put on this schedule. Its cadence, cutoff and pay date can no longer change once it is used; a different rule becomes a replacement schedule.",
    confirm: "Activate pay schedule",
  },
  archive: {
    title: (s) => `Archive "${s.name}"?`,
    consequence: "It stops paying anyone and creates no new payroll periods. Its past periods, payslips and history stay readable. You can restore it later.",
    confirm: "Archive pay schedule",
  },
  restore: {
    title: (s) => `Restore "${s.name}"?`,
    consequence: "It becomes active again from its current period. It never pays the dates it was archived for; employees are put back on it one by one or in bulk.",
    confirm: "Restore pay schedule",
  },
  makeDefault: {
    title: (s) => `Make "${s.name}" the schedule new employees start on?`,
    consequence: "New employees are put on it when their pay is first set. Nobody already on another schedule is moved.",
    confirm: "Make default",
  },
};
